/* BellePepper Studio. No trackers; no dependencies. Native Shopify links remain the fallback. */
(() => {
  'use strict';
  if (window.BellePepperStudio) return;
  window.BellePepperStudio = {version: '3.3'};
  let blogRequest = null;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let motionObserver = null;
  let heroMotionQueued = false;

  function selectRitual(tab) {
    const root = tab.closest('[data-bpc-tabs]');
    if (!root) return;
    root.querySelectorAll('[data-bpc-tab]').forEach(item => {
      const active = item === tab;
      item.setAttribute('aria-selected', String(active));
      item.tabIndex = active ? 0 : -1;
    });
    root.querySelectorAll('[data-bpc-panel]').forEach(panel => {
      panel.hidden = panel.dataset.bpcPanel !== tab.dataset.bpcTab;
    });
  }

  function selectRawBenefit(tab, {focus = false} = {}) {
    const root = tab.closest('[data-bpc-raw]');
    if (!root) return;
    root.classList.add('is-enhanced');
    const id = tab.dataset.bpcRawTab;
    root.querySelectorAll('[data-bpc-raw-tab]').forEach(item => {
      const active = item === tab;
      item.setAttribute('aria-selected', String(active));
      item.tabIndex = active ? 0 : -1;
    });
    root.querySelectorAll('[data-bpc-raw-panel]').forEach(panel => {
      const active = panel.dataset.bpcRawPanel === id;
      panel.hidden = !active;
      panel.classList.toggle('is-active', active);
    });
    if (focus) tab.focus();
  }

  function initRawBenefits(scope = document) {
    const roots = [];
    if (scope.matches?.('[data-bpc-raw]')) roots.push(scope);
    scope.querySelectorAll?.('[data-bpc-raw]').forEach(root => roots.push(root));
    roots.forEach(root => {
      if (root.dataset.bpcRawReady === 'true') return;
      root.dataset.bpcRawReady = 'true';
      const selected = root.querySelector('[data-bpc-raw-tab][aria-selected="true"]') || root.querySelector('[data-bpc-raw-tab]');
      if (selected) selectRawBenefit(selected);
    });
  }

  function ensureMotionObserver() {
    if (motionObserver || reducedMotion.matches || !('IntersectionObserver' in window)) return motionObserver;
    motionObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        motionObserver.unobserve(entry.target);
      });
    }, {rootMargin: '0px 0px -7% 0px', threshold: 0.08});
    return motionObserver;
  }

  function initMotion(scope = document) {
    const selector = [
      '.bpc-proof-item',
      '.bpc-product',
      '.bpc-choice',
      '.bpc-fact',
      '.bpc-tab',
      '.bpc-article',
      '.bpc-treat-compare-row',
      '.bpc-raw-benefit-tab',
      '.bpc-raw-principle',
      '.bpc-carnivore-card',
      '.bpc-transition-step',
      '.bpc-faq-list details'
    ].join(',');
    const observer = ensureMotionObserver();
    const items = scope.querySelectorAll?.(selector) || [];
    items.forEach((item, index) => {
      if (item.dataset.bpcMotionReady === 'true') return;
      item.dataset.bpcMotionReady = 'true';
      if (reducedMotion.matches || !observer) {
        item.classList.add('is-visible');
        return;
      }
      item.classList.add('bpc-motion-item');
      item.style.setProperty('--bpc-motion-delay', `${Math.min(index % 5, 4) * 65}ms`);
      const rect = item.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.92) item.classList.add('is-visible');
      else observer.observe(item);
    });
  }

  function updateHeroMotion() {
    heroMotionQueued = false;
    if (reducedMotion.matches) return;
    document.querySelectorAll('.bpc-hero').forEach(hero => {
      const roundel = hero.querySelector('.bpc-roundel');
      if (!roundel) return;
      const rect = hero.getBoundingClientRect();
      if (rect.bottom < -120 || rect.top > window.innerHeight + 120) return;
      const travel = Math.max(1, window.innerHeight + rect.height);
      const progress = Math.max(0, Math.min(1, (window.innerHeight - rect.top) / travel));
      const spin = (progress - 0.42) * 24;
      const float = (progress - 0.5) * -9;
      roundel.style.setProperty('--bpc-scroll-spin', `${spin.toFixed(2)}deg`);
      roundel.style.setProperty('--bpc-scroll-float', `${float.toFixed(2)}px`);
    });
  }

  function queueHeroMotion() {
    if (heroMotionQueued || reducedMotion.matches) return;
    heroMotionQueued = true;
    requestAnimationFrame(updateHeroMotion);
  }

  let floatingShopFooterObserver = null;

  function initFloatingShop() {
    const root = document.querySelector('[data-bpc-floating-shop]');
    if (!root) return;
    if (root.dataset.bpcFloatingReady !== 'true') {
      root.dataset.bpcFloatingReady = 'true';
      const update = () => root.classList.toggle('is-visible', window.scrollY > 150 || (window.Shopify && window.Shopify.designMode));
      update();
      window.addEventListener('scroll', update, {passive: true});
    }
    if (floatingShopFooterObserver) floatingShopFooterObserver.disconnect();
    const footer = document.querySelector('footer.footer') || document.querySelector('footer');
    if (footer && 'IntersectionObserver' in window) {
      floatingShopFooterObserver = new IntersectionObserver(entries => {
        root.classList.toggle('is-near-footer', entries.some(entry => entry.isIntersecting));
      }, {rootMargin: '0px 0px 70px 0px', threshold: 0});
      floatingShopFooterObserver.observe(footer);
    }
  }

  async function loadBlog(url, {push = true, moveFocus = true, showResults = false} = {}) {
    const root = document.querySelector('[data-bpc-blog]');
    if (!root || !root.dataset.sectionId) { window.location.assign(url); return; }
    const destination = new URL(url, window.location.href);
    if (destination.origin !== window.location.origin) { window.location.assign(url); return; }
    if (blogRequest) blogRequest.abort();
    const controller = new AbortController();
    blogRequest = controller;
    const grid = root.querySelector('.bpc-blog-grid');
    const status = root.querySelector('[data-bpc-blog-status]');
    if (grid) grid.setAttribute('aria-busy', 'true');
    if (status) status.textContent = 'Loading stories.';
    const request = new URL(destination);
    request.searchParams.set('section_id', root.dataset.sectionId);
    const scrollLeft = root.querySelector('.bpc-topic-nav')?.scrollLeft || 0;
    try {
      const response = await fetch(request, {signal: controller.signal, credentials: 'same-origin', headers: {'X-Requested-With': 'XMLHttpRequest'}});
      if (!response.ok) throw new Error('Unable to load blog section.');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const replacement = doc.querySelector('[data-bpc-blog]');
      if (!replacement) throw new Error('Blog section was not returned.');
      if (controller.signal.aborted) return;
      root.replaceWith(replacement);
      const topics = replacement.querySelector('.bpc-topic-nav');
      if (topics) topics.scrollLeft = scrollLeft;
      if (push) window.history.pushState({bpcBlog: true}, '', destination.pathname + destination.search + destination.hash);
      const active = replacement.querySelector('.bpc-topic[aria-current="page"]');
      if (active) document.title = `${active.textContent.trim()} | BellePepper Cat Education`;
      const newStatus = replacement.querySelector('[data-bpc-blog-status]');
      const count = replacement.querySelector('.bpc-blog-toolbar')?.textContent.trim() || 'Stories loaded.';
      if (newStatus) newStatus.textContent = count;
      initMotion(replacement);
      if (moveFocus) {
        const focusTarget = showResults ? replacement.querySelector('.bpc-blog-grid') : (active || replacement.querySelector('.bpc-blog-grid'));
        focusTarget?.focus({preventScroll: true});
        if (showResults) focusTarget?.scrollIntoView({block: 'start', behavior: reducedMotion.matches ? 'instant' : 'smooth'});
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      window.location.assign(destination.href);
    } finally {
      if (blogRequest === controller) blogRequest = null;
    }
  }

  document.addEventListener('click', event => {
    const choice = event.target.closest('[data-bpc-choice]');
    if (choice) {
      const root = choice.closest('[data-bpc-finder]');
      if (!root) return;
      root.querySelectorAll('[data-bpc-choice]').forEach(button => button.setAttribute('aria-pressed', String(button === choice)));
      root.querySelectorAll('[data-bpc-result]').forEach(result => { result.hidden = result.dataset.bpcResult !== choice.dataset.bpcChoice; });
      const selected = root.querySelector(`[data-bpc-result="${choice.dataset.bpcChoice}"] h3`);
      const status = root.querySelector('[data-bpc-finder-status]');
      if (status) status.textContent = selected ? `Your starting point: ${selected.textContent.trim()}.` : 'Explore this preference.';
      return;
    }
    const rawTab = event.target.closest('[data-bpc-raw-tab]');
    if (rawTab) { selectRawBenefit(rawTab); return; }
    const tab = event.target.closest('[data-bpc-tab]');
    if (tab) { selectRitual(tab); return; }
    const link = event.target.closest('a[data-bpc-blog-link]');
    if (link && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey && event.button === 0 && (!link.target || link.target === '_self')) {
      event.preventDefault();
      loadBlog(link.href, {showResults: Boolean(link.closest('.bpc-pagination'))});
    }
  });

  document.addEventListener('keydown', event => {
    const rawTab = event.target.closest('[data-bpc-raw-tab]');
    if (rawTab && ['ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End'].includes(event.key)) {
      const tabs = Array.from(rawTab.closest('[data-bpc-raw]').querySelectorAll('[data-bpc-raw-tab]'));
      let index = tabs.indexOf(rawTab);
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') index = (index + 1) % tabs.length;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') index = (index - 1 + tabs.length) % tabs.length;
      if (event.key === 'Home') index = 0;
      if (event.key === 'End') index = tabs.length - 1;
      event.preventDefault();
      selectRawBenefit(tabs[index], {focus: true});
      return;
    }
    const tab = event.target.closest('[data-bpc-tab]');
    if (!tab || !['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
    const tabs = Array.from(tab.closest('[data-bpc-tabs]').querySelectorAll('[data-bpc-tab]'));
    let index = tabs.indexOf(tab);
    if (event.key === 'ArrowRight') index = (index + 1) % tabs.length;
    if (event.key === 'ArrowLeft') index = (index - 1 + tabs.length) % tabs.length;
    if (event.key === 'Home') index = 0;
    if (event.key === 'End') index = tabs.length - 1;
    event.preventDefault();
    selectRitual(tabs[index]);
    tabs[index].focus();
  });

  document.addEventListener('shopify:block:select', event => {
    const blockId = event.detail && event.detail.blockId;
    if (!blockId) return;
    const rawBenefit = document.querySelector(`[data-bpc-raw-tab="${CSS.escape(blockId)}"]`);
    if (rawBenefit) { selectRawBenefit(rawBenefit); return; }
    const selected = document.querySelector(`[data-bpc-block-id="${CSS.escape(blockId)}"]`);
    if (!selected) return;
    const finderChoice = selected.matches('[data-bpc-choice]') ? selected : selected.closest('[data-bpc-finder]')?.querySelector(`[data-bpc-choice="${CSS.escape(blockId)}"]`);
    if (finderChoice) { finderChoice.click(); return; }
    const ritualTab = selected.matches('[data-bpc-tab]') ? selected : selected.closest('[data-bpc-tabs]')?.querySelector(`[data-bpc-tab="${CSS.escape(blockId)}"]`);
    if (ritualTab) selectRitual(ritualTab);
  });

  document.addEventListener('shopify:section:load', event => {
    initRawBenefits(event.target);
    initMotion(event.target);
    initFloatingShop();
    queueHeroMotion();
  });

  window.addEventListener('popstate', () => {
    if (document.querySelector('[data-bpc-blog]')) loadBlog(window.location.href, {push: false, moveFocus: false});
  });
  window.addEventListener('scroll', queueHeroMotion, {passive: true});
  window.addEventListener('resize', queueHeroMotion, {passive: true});

  function init() {
    initRawBenefits(document);
    initMotion(document);
    initFloatingShop();
    updateHeroMotion();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, {once: true});
  else init();
})();
